'use client';
import React, { useState } from 'react';
import { Check, Sparkles, ArrowRight, Zap } from 'lucide-react';
import Link from 'next/link';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  desc: string;
  features: string[];
  cta: string;
  highlighted: boolean;
  badge?: string;
  color: string;
  gradient?: string;
}

const tiers: PricingTier[] = [
  {
    name: 'Starter',
    price: '$49',
    period: '/month',
    desc: 'Perfect for individual agents getting started with AI.',
    features: [
      'AI Command Center',
      'Morning Brief & Wrap Up',
      'Up to 500 contacts',
      'Email drafting',
      'Basic follow-up reminders',
      'Mobile access',
    ],
    cta: 'Request Early Access',
    highlighted: false,
    color: '#00D4FF',
  },
  {
    name: 'Professional',
    price: '$99',
    period: '/month',
    desc: 'For active agents who want the full AI business partner experience.',
    features: [
      'Everything in Starter',
      'Unlimited contacts',
      'Catch Me Up & full memory',
      'AI-drafted marketing content',
      'Social media scheduling',
      'Priority support',
      'Listing descriptions & emails',
    ],
    cta: 'Request Early Access',
    highlighted: true,
    badge: 'Most Popular',
    color: '#00D4FF',
    gradient: 'linear-gradient(135deg, #00D4FF, #0099CC)',
  },
  {
    name: 'Team',
    price: '$199',
    period: '/month',
    desc: 'For growing teams that need shared tools and consistent branding.',
    features: [
      'Everything in Professional',
      'Up to 5 team members',
      'Shared contact database',
      'Team performance overview',
      'Brand voice consistency',
      'Admin controls',
    ],
    cta: 'Request Early Access',
    highlighted: false,
    color: '#A78BFA',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For brokerages and large teams with advanced needs.',
    features: [
      'Everything in Team',
      'Unlimited team members',
      'Custom integrations',
      'Dedicated onboarding',
      'SLA & uptime guarantee',
      'Custom AI training',
      'White-label options',
    ],
    cta: 'Contact Us',
    highlighted: false,
    color: '#A78BFA',
  },
];

export default function PricingSection() {
  const [annual, setAnnual] = useState(false);

  return (
    <section id="pricing" className="py-24 section-alt-bg relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)' }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-14">
          <div className="section-badge mb-4">
            <Sparkles size={12} />
            Founding Member Pricing
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4"
            style={{ color: '#F0F4FF' }}
          >
            Founding member pricing.{' '}
            <span className="gradient-cyan-text">Lock it in now.</span>
          </h2>
          <p className="text-lg max-w-xl mx-auto mb-2" style={{ color: 'rgba(240,244,255,0.55)' }}>
            Pricing is being finalized. Founding members lock in the lowest rate — forever.
          </p>
          <p className="text-sm max-w-xl mx-auto mb-8" style={{ color: 'rgba(240,244,255,0.35)' }}>
            No contracts. Cancel anytime. Every plan includes a free trial.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 rounded-xl p-1.5" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
            <button
              onClick={() => setAnnual(false)}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={!annual ? {
                background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                color: '#0B0F19',
              } : {
                color: 'rgba(240,244,255,0.55)',
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
              style={annual ? {
                background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                color: '#0B0F19',
              } : {
                color: 'rgba(240,244,255,0.55)',
              }}
            >
              Annual
              <span
                className="text-xs px-2 py-0.5 rounded-full font-bold"
                style={{ background: 'rgba(34,197,94,0.20)', color: '#22C55E' }}
              >
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className="rounded-3xl p-7 flex flex-col relative"
              style={tier.highlighted ? {
                background: 'linear-gradient(135deg, rgba(0,212,255,0.10) 0%, rgba(0,212,255,0.04) 100%)',
                border: '2px solid rgba(0,212,255,0.30)',
                boxShadow: '0 8px 40px rgba(0,212,255,0.12)',
              } : {
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              {tier.badge && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-4 py-1.5 rounded-full"
                  style={{
                    background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                    color: '#0B0F19',
                    fontFamily: 'JetBrains Mono, monospace',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {tier.badge}
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg font-extrabold mb-1" style={{ color: '#F0F4FF' }}>{tier.name}</h3>
                <p className="text-sm mb-4" style={{ color: 'rgba(240,244,255,0.50)' }}>{tier.desc}</p>
                <div className="flex items-end gap-1">
                  <span
                    className="text-4xl font-extrabold"
                    style={{ color: tier.highlighted ? '#00D4FF' : '#F0F4FF', fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {tier.price === 'Custom' ? tier.price : annual ? `$${Math.round(parseInt(tier.price.replace('$', '')) * 0.8)}` : tier.price}
                  </span>
                  {tier.period && (
                    <span className="text-sm mb-1" style={{ color: 'rgba(240,244,255,0.40)' }}>{tier.period}</span>
                  )}
                </div>
                {annual && tier.price !== 'Custom' && (
                  <p className="text-xs mt-1" style={{ color: '#22C55E' }}>Billed annually</p>
                )}
              </div>

              <ul className="space-y-3 flex-1 mb-7">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={tier.highlighted ? {
                        background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                      } : {
                        background: `${tier.color}18`,
                        border: `1px solid ${tier.color}30`,
                      }}
                    >
                      <Check size={10} strokeWidth={3} style={{ color: tier.highlighted ? '#0B0F19' : tier.color }} />
                    </div>
                    <span className="text-sm" style={{ color: 'rgba(240,244,255,0.70)' }}>{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href={tier.name === 'Enterprise' ? '#contact' : 'https://agentos-ai-vgojf93.public.builtwithrocket.new/'}
                target={tier.name === 'Enterprise' ? undefined : '_blank'}
                rel={tier.name === 'Enterprise' ? undefined : 'noopener noreferrer'}
                onClick={tier.name === 'Enterprise' ? (e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                } : undefined}
                className="w-full text-center font-bold py-3 px-5 rounded-xl transition-all inline-flex items-center justify-center gap-2 text-sm"
                style={tier.highlighted ? {
                  background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                  color: '#0B0F19',
                  boxShadow: '0 4px 20px rgba(0,212,255,0.35)',
                } : {
                  background: 'rgba(255,255,255,0.08)',
                  color: '#F0F4FF',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
                onMouseEnter={(e) => {
                  if (!tier.highlighted) {
                    (e.currentTarget as HTMLElement).style.background = `${tier.color}15`;
                    (e.currentTarget as HTMLElement).style.borderColor = `${tier.color}35`;
                    (e.currentTarget as HTMLElement).style.color = tier.color;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!tier.highlighted) {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.08)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.12)';
                    (e.currentTarget as HTMLElement).style.color = '#F0F4FF';
                  }
                }}
              >
                {tier.highlighted && <Zap size={14} strokeWidth={2.5} />}
                {tier.cta}
                {!tier.highlighted && <ArrowRight size={14} />}
              </a>
            </div>
          ))}
        </div>

        {/* Guarantees */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
          {['No Contracts', 'Cancel Anytime', 'Transparent Pricing', 'Free Trial Included'].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(240,244,255,0.55)' }}>
              <Check size={14} style={{ color: '#22C55E' }} />
              <span>{item}</span>
            </div>
          ))}
        </div>

        {/* See in Action link */}
        <div className="mt-8 text-center">
          <Link
            href="/in-action"
            className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
            style={{ color: 'rgba(0,212,255,0.70)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#00D4FF'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(0,212,255,0.70)'; }}
          >
            See AgentOS in Action before you decide →
          </Link>
        </div>
      </div>
    </section>
  );
}
